import { useMemo } from 'react';
import { Alert, Container } from 'react-bootstrap';
import { useQuery } from '@apollo/client';

import { GetProgressiveAdsConfigsQuery, PraSection } from '../../../../../__gqltypes__';
import { LoadingLogo } from '../../../devtools/components/Modal';
import { PraSectionCard } from './components/PraSectionCard';
import { GET_PROGRESSIVE_ADS_CONFIGS } from './graphql';

// Progressive Rewarded Ads — a singleton config per namespace (two section cards, not an
// event list). The query/mutations omit the namespace: the server defaults to its own.
export default function ProgressiveAds(): JSX.Element {
  const { data, loading, error } = useQuery<GetProgressiveAdsConfigsQuery>(GET_PROGRESSIVE_ADS_CONFIGS, {
    fetchPolicy: 'no-cache',
  });

  const configsBySection = useMemo(() => {
    const list = data?.liveOps?.progressiveAdsConfigs ?? [];
    return {
      [PraSection.FREE]: list.find((config) => config.section === PraSection.FREE) ?? null,
      [PraSection.CHAINED]: list.find((config) => config.section === PraSection.CHAINED) ?? null,
    };
  }, [data]);

  if (loading) {
    return <LoadingLogo show />;
  }

  const namespace =
    configsBySection[PraSection.FREE]?.namespace ?? configsBySection[PraSection.CHAINED]?.namespace ?? null;

  return (
    <Container className="pb-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Progressive Rewarded Ads</h4>
        <span className="text-muted">Namespace: {namespace ?? 'server default (set on first save)'}</span>
      </div>
      {error && <Alert variant="danger">Failed to load the configuration: {error.message}</Alert>}
      <PraSectionCard section={PraSection.FREE} initialConfig={configsBySection[PraSection.FREE]} />
      <PraSectionCard section={PraSection.CHAINED} initialConfig={configsBySection[PraSection.CHAINED]} />
    </Container>
  );
}
